// src/pages/agenda/estilo.js — cores, tipos e regras visuais da Agenda.
//
// Out/26, fiel ao AgendaTela.dc.html. Cor tem significado: cada tipo de
// compromisso tem a sua, e o status usa o tom que vem pronto do servidor
// (green/amber/red/blue/neutro) — a tela não decide se algo está atrasado.

export const FONT_TXT = "'Syne', 'Segoe UI', sans-serif";
export const FONT_NUM = "'Outfit', sans-serif";

export const TIPOS = {
  consulta: { label: null, um: null, c: 'green' }, // nome vem da empresa (AgendaConfig)
  disparo: { label: 'Disparos de template', um: 'Disparo de template', c: 'blue' },
  lembrete: { label: 'Lembretes do CRM', um: 'Lembrete do CRM', c: 'amber' },
  tarefa: { label: 'Tarefas internas', um: 'Tarefa interna', c: 'pink' },
};
export const ORDEM_TIPOS = ['consulta', 'disparo', 'lembrete', 'tarefa'];

export const cor = (c) => ({
  bg: `var(--ch-${c}-bg)`, b: `var(--ch-${c}-b)`, d: `var(--ch-${c}-d)`, base: `var(--ch-${c})`,
});

// Rótulos com o nome da consulta escolhido pela empresa ("Reuniões").
export function rotuloTipo(tipo, nome) {
  if (tipo === 'consulta') return nome?.plural || 'Consultas';
  return TIPOS[tipo]?.label || tipo;
}
export function umTipo(tipo, nome) {
  if (tipo === 'consulta') return nome?.singular || 'Consulta';
  return TIPOS[tipo]?.um || tipo;
}

export function tomStatus(tom) {
  if (!tom || tom === 'neutro') return { cor: 'var(--ch-text2)', bg: 'var(--ch-inset)', b: 'var(--ch-bdr)' };
  // Fase 3: proposta do Copiloto, ainda não confirmada.
  if (tom === 'lilas') return { cor: 'var(--cp-claro)', bg: 'var(--cp-tint)', b: 'var(--cp-b)' };
  return { cor: `var(--ch-${tom}-d)`, bg: `var(--ch-${tom}-bg)`, b: `var(--ch-${tom}-b)` };
}

// Avatar: 8 pares fixos do ChatPage.css (--ch-avN-*), escolhidos por hash
// do id — a mesma pessoa tem a mesma cor em toda tela.
export function avatar(id) {
  let h = 0;
  const s = String(id || '');
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  const n = (h % 8) + 1;
  return { bg: `var(--ch-av${n}-bg)`, fg: `var(--ch-av${n}-fg)` };
}

export const primeiroNome = (nome) => (nome || '').split(' ')[0];

// Fase 3: a proposta do Copiloto é desenhada tracejada, em lilás, até
// alguém confirmar (design: "Proposto pelo Copiloto").
export const LILAS = '#9184d9';
export const ehProposta = (e) => !!e?.proposta;
export function corDoBloco(e, k) {
  if (!ehProposta(e)) return { bg: k.bg, borda: `1px solid ${k.b}`, texto: k.d, base: k.base };
  return { bg: 'var(--cp-tint)', borda: `1.5px dashed ${LILAS}`, texto: 'var(--cp-claro)', base: LILAS };
}

// Faixa de horário: disparo é um instante (só a hora); o resto, início–fim.
export const faixa = (e) => (e.tipo === 'disparo' ? e.ini : `${e.ini}–${e.fim}`);

// Compromissos que se sobrepõem ficam lado a lado: cada um ganha uma
// "faixa" dentro do grupo de sobreposição (mesma conta do design).
export function faixas(evs) {
  const ordem = [...evs].sort((a, b) => a.ini_h - b.ini_h || b.fim_h - a.fim_h);
  const out = [];
  let grupo = []; let fimGrupo = -1; let trilhos = [];
  const fecha = () => { const n = trilhos.length || 1; grupo.forEach((x) => { x.n = n; }); grupo = []; trilhos = []; fimGrupo = -1; };
  ordem.forEach((e) => {
    if (grupo.length && e.ini_h >= fimGrupo) fecha();
    let lane = trilhos.findIndex((f) => f <= e.ini_h);
    if (lane < 0) { lane = trilhos.length; trilhos.push(e.fim_h); } else trilhos[lane] = e.fim_h;
    const it = { e, lane, n: 1 };
    grupo.push(it); out.push(it); fimGrupo = Math.max(fimGrupo, e.fim_h);
  });
  fecha();
  return out;
}

export const botaoBase = {
  fontFamily: FONT_TXT, cursor: 'pointer',
  '&:focus-visible': { outline: '2px solid #9184d9', outlineOffset: 2 },
  '&:disabled': { cursor: 'default', opacity: 0.6 },
};
export const botaoPrimario = {
  ...botaoBase, fontSize: '0.74rem', fontWeight: 700, color: 'var(--ch-on-green)', bgcolor: 'var(--ch-green)',
  border: 'none', borderRadius: '6px', p: '8px 12px', '&:hover': { bgcolor: 'var(--ch-green-hover)' },
};
export const botaoSecundario = {
  ...botaoBase, fontSize: '0.74rem', fontWeight: 600, color: 'var(--ch-text2)', bgcolor: 'transparent',
  border: '1px solid var(--ch-bdr2)', borderRadius: '6px', p: '8px 12px',
  '&:hover': { color: 'var(--ch-text)', bgcolor: 'var(--ch-inset)' },
};
export const cartao = {
  bgcolor: 'var(--ch-card)', border: '1px solid var(--ch-bdr)', borderRadius: '12px', boxShadow: 'var(--sh-sm)',
};
export const rotuloSecao = {
  fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.18em', color: 'var(--ch-text3)', textTransform: 'uppercase',
};
// No dedo (celular e tablet), botão com pelo menos 40px de altura.
export const DEDO = '@media (pointer: coarse)';
